import {captureMessage} from '@sentry/react';
import {
    BlsDataType,
    BusinessCardDataType,
    FORM_TYPE,
    FormDataType,
    LeadFormDataType,
    SurveyDataType,
    YClientsDataType,
} from 'api/types/LeadForm';
import {
    MergedFillData,
    LoadResourceError,
    LoadResourceErrorType,
    LoadResourceMetrics,
    ResourceMetaType,
    VIEW,
    VKWebAppGetCustomSdkUserInfoResult,
} from './types';
import {ContactField, FormDataTypeGradient} from '@target-web/leadads-form-view';
import {apiUrl, isDev, USER_ID_FROM} from './constants';
import {Cookie} from './cookie';
import {formatVKDate} from './formatVKDate';
import {FullUserInfo} from './userInfoContext';

// тк прила крутится в вебвью - на андроидах по умолчанию не включен localStorage, в этом случае переходим на куки
export const Storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = localStorage || Cookie;

export const getVKAwayLink = (link: string) => `https://vk.com/away.php?to=${encodeURIComponent(link)}`;

// в вебе VK хеш начинается с #?
// в вебе OK хеш начинается просто с #
// в бридже из VKWebAppChangeFragment хеш иногда приходит без решетки, начинается с ?
export const getCurrentHash = (forceHash?: string) => {
    return (forceHash || window.location.hash).replace(/^(#\?|#&|#|\?|&)/g, '');
};

// копипаста метода используется в index.html, меняя не забудь заглянуть туда
export const getCurrentSearch = (forceHash?: string) => {
    const search = window.location.search;
    const hash = getCurrentHash(forceHash) || getCurrentHash();

    return search ? search + '&' + hash : '?' + hash;
};

export const hasClientInUrl = () => {
    const params = new URLSearchParams(getCurrentSearch());
    return params.has('vk_client');
};

export const isVkClient = () => {
    const params = new URLSearchParams(getCurrentSearch());
    return params.get('vk_client') === USER_ID_FROM.VK;
};

export const isOKClient = () => {
    const params = new URLSearchParams(getCurrentSearch());

    return params.get('vk_client') === USER_ID_FROM.OK;
};

export const isMailClient = () => {
    const params = new URLSearchParams(getCurrentSearch());

    return params.get('vk_client') === USER_ID_FROM.MAIL;
};

export const isSdkClient = () => {
    const params = new URLSearchParams(getCurrentSearch());

    return params.get('vk_client') === USER_ID_FROM.SDK;
};

export const hasVkAppId = () => {
    const params = new URLSearchParams(getCurrentSearch());

    return params.has('vk_app_id');
};

export const isUseCustomBridge = () => {
    const params = new URLSearchParams(getCurrentSearch());

    return params.get('use_custom_bridge') === '1';
};

export const isMobile = () =>
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

export const isEnumMember = <Value extends string | number>(
    target: Record<PropertyKey, Value>,
    value: string | number,
): value is Value => {
    const values: unknown[] = Object.values(target);
    return values.includes(value);
};

export const getCurrentSearchParams = (searchStr: string) => {
    const search = searchStr.replace(/amp%3B/g, '') || getCurrentSearch();
    const p = new URLSearchParams(search);

    const view = p.get('view');
    const formDataHash = p.get('d');
    const formDataType = p.get('type');
    const vk_client = p.get('vk_client');
    const vk_platform = p.get('vk_platform');
    const vk_ref = p.get('vk_ref');
    const service_id = p.get('service_id');

    let platform = 'vk_cdn';
    if (isDev) {
        platform = 'local';
    } else if (process.env.IS_KUBER_BUILD) {
        platform = 'kuber';
    }

    if (view && !isEnumMember(VIEW, view)) {
        captureMessage('Passed as unknown view value', 'warning');
    }

    return {
        formDataHash,
        formDataType: formDataType && isEnumMember(FORM_TYPE, formDataType) ? formDataType : FORM_TYPE.leadform,
        isLeadFormType: !formDataType || formDataType === FORM_TYPE.leadform,
        isSurveyType: formDataType === FORM_TYPE.survey,
        isYclientsType: formDataType === FORM_TYPE.yclients,
        isBusinessCardType: formDataType === FORM_TYPE.business_card,
        isBlsType: formDataType === FORM_TYPE.bls,
        view: view && isEnumMember(VIEW, view) ? view : null,
        vk_client: vk_client && isEnumMember(USER_ID_FROM, vk_client) ? vk_client : USER_ID_FROM.VK,
        onlyAgreement: p.get('agreement-only') === '1',
        vk_ref,
        vk_platform,
        vk_app_id: p.get('vk_app_id'), // для ОК и ВК они разные
        vk_user_id: p.get('vk_user_id'),
        forced_filling: p.get('forced_filling') === '1',
        isDarkThemeParam: p.get('isDarkTheme'),
        platform, // место где запускается прила. local | kuber | vk_cdn
        isTest: p.get('test') === '1', // используется для тестового заполнения из каба рекла
        serviceId: service_id && /^\d+$/.test(service_id) ? Number(service_id) : null,
        isVkClient: p.get('vk_client') === USER_ID_FROM.VK,
        isOkClient: p.get('vk_client') === USER_ID_FROM.OK,
        isMailClient: p.get('vk_client') === USER_ID_FROM.MAIL,
        isSdkClient: p.get('vk_client') === USER_ID_FROM.SDK,
    };
};

export type searchParamsWithFormDataHash = Omit<ReturnType<typeof getCurrentSearchParams>, 'formDataHash'> & {
    formDataHash: string;
};

export const debounce = <T extends (...args: any[]) => ReturnType<T>>(
    callback: T,
    timeout: number = 300,
): ((...args: Parameters<T>) => void) => {
    let timer: ReturnType<typeof setTimeout>;

    return (...args: Parameters<T>) => {
        clearTimeout(timer);

        timer = setTimeout(() => {
            callback(...args);
        }, timeout);
    };
};

// пока не используется
// export function debouncePromise<T extends (...args: any[]) => any>(callback: T, timeout = 300) {
//     let cancel = () => {};
//     type ReturnT = Awaited<ReturnType<T>>;

//     return (...args: Parameters<T>): Promise<ReturnT> => {
//         cancel();

//         return new Promise((resolve, reject) => {
//             const timer = setTimeout(() => resolve(callback(...args)), timeout);

//             cancel = () => {
//                 clearTimeout(timer);
//                 reject('debounced');
//             };
//         });
//     };
// }

export const isBusinessCard = (formData: FormDataType): formData is BusinessCardDataType => {
    return formData.type === FORM_TYPE.business_card;
};

export const isLeadForm = (formData: FormDataType): formData is LeadFormDataType => {
    return formData.type === FORM_TYPE.leadform;
};

export const isSurvey = (formData: FormDataType): formData is SurveyDataType => {
    return formData.type === FORM_TYPE.survey;
};

export const isYClients = (formData: FormDataType): formData is YClientsDataType => {
    return formData.type === FORM_TYPE.yclients;
};

export const isBls = (formData: FormDataType): formData is BlsDataType => {
    return formData.type === FORM_TYPE.bls;
};

export function assertGradient(gradient: number, main_color?: string): FormDataTypeGradient {
    if (gradient === 101) {
        if (!main_color) {
            throw new Error(`Формат поля gradient нарушен`);
        }
        return {
            gradient,
            main_color,
        };
    }

    if (
        gradient === 0 ||
        gradient === 1 ||
        gradient === 2 ||
        gradient === 3 ||
        gradient === 4 ||
        gradient === 5 ||
        gradient === 6 ||
        gradient === 100
    ) {
        return {gradient};
    }
    throw new Error(`Формат поля gradient нарушен`);
}

export const formatPhone = (phone: string) => phone.replace(/[^0-9+]/g, '');

export const getLeadFormRequestUrl = (params: searchParamsWithFormDataHash, hash?: string) => {
    const formTypeUrl =
        params.formDataType === FORM_TYPE.leadform
            ? '/leads'
            : params.formDataType === FORM_TYPE.business_card
            ? '/business_card'
            : params.formDataType === FORM_TYPE.bls
            ? '/bls'
            : params.formDataType === FORM_TYPE.survey
            ? '/survey'
            : '/yclients/form';

    const formDataHash = hash || params.formDataHash;

    const searchStr = new URLSearchParams({
        lead_form_data: formDataHash,
        session_id: Storage.getItem(formDataHash) || '', // для восстановления состояния заполнения
        platform: params.platform,
        ...(params.vk_ref ? {vk_ref: params.vk_ref} : {}),
        ...(params.vk_platform ? {vk_platform: params.vk_platform} : {}),
        ...(params.vk_app_id ? {vk_app_id: params.vk_app_id} : {}),
        ...(params.vk_user_id ? {user_id: params.vk_user_id} : {}),
    });

    return `${apiUrl}${formTypeUrl}?${searchStr}`;
};

export const initSideEffects = ({formDataHash, isTest}: ReturnType<typeof getCurrentSearchParams>) => {
    if (isTest && formDataHash) {
        // если рекл открывает тестовый опрос - то сбрасываем sessionId чтоб опрос начинался сначала
        Storage.removeItem(formDataHash);
    }
    if (!formDataHash) {
        captureMessage('Required parameter D not received', 'info');
    }
};

const formatFilledPhone = (phone: string) => {
    if (!!Number(phone)) {
        // если номер из цифр - отформатируем. Преобразует номера только из 11 цифр
        return phone.replace(/\D+/g, '').replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+7 ($2) $3-$4');
    }
    if (phone.startsWith('7')) {
        return '+' + phone;
    }

    return phone;
};

export const getContactInfoValue = (userInfo: FullUserInfo, field: ContactField) => {
    switch (field) {
        case 'first_name':
            return userInfo?.general?.first_name;
        case 'email':
            return userInfo?.email;
        case 'phone':
            return userInfo?.phone ? formatFilledPhone(userInfo.phone) : undefined;
        case 'social_media_profile':
            return userInfo?.general?.vk_id
                ? `https://vk.com/id${userInfo?.general?.vk_id}`
                : userInfo?.general?.ok_id
                ? `https://ok.ru/profile/${userInfo?.general?.ok_id}`
                : undefined;
        case 'birth_date':
            return userInfo?.general?.bdate?.split('.')?.length === 3
                ? formatVKDate(userInfo.general.bdate)
                : undefined;
        case 'city':
            return userInfo?.general?.city?.title;
        default:
            return '';
    }
};

export const getUserPhoneDataDecodeParams = (userInfo: FullUserInfo) => {
    const user_id = userInfo.general?.vk_id || userInfo.general?.ok_id;

    return {
        ...(user_id ? {user_id: user_id.toString()} : {}),
        user_id_type: userInfo.phone_data?.app_id,
        access_token: userInfo.phone_data?.access_token,
    };
};

export const expandUserInfoWithDataFromSdk = (
    userInfo: FullUserInfo,
    sdkData: VKWebAppGetCustomSdkUserInfoResult,
    vkData?: VKWebAppGetCustomSdkUserInfoResult,
): FullUserInfo => {
    const phoneEmailData: MergedFillData = {};

    for (const field of ['phone', 'email'] as const) {
        // данные из вк имеют приоритет над данными из сдк
        // если от вк пришел телефон/почта
        if (vkData && vkData[field]) {
            phoneEmailData[field] = vkData[field];
            // если он маскированный
            if (vkData[`${field}_sign`]) {
                // decode_params обязательно должны быть
                if (!vkData.decode_params) {
                    captureMessage('vk decode_params is not provided', 'error');
                    continue;
                }

                // добавим данные для расшифровки телефона из вк/почты
                phoneEmailData[`${field}_sign`] = vkData[`${field}_sign`];
                phoneEmailData[`${field}_data`] = vkData.decode_params;
            }
        } else if (sdkData[field]) {
            phoneEmailData[field] = sdkData[field];
            // если он маскированный
            if (sdkData[`${field}_sign`]) {
                // decode_params обязательно должны быть
                if (!sdkData.decode_params) {
                    captureMessage('sdk decode_params is not provided', 'error');
                    continue;
                }

                // добавим данные для расшифровки телефона из сдк
                phoneEmailData[`${field}_sign`] = sdkData[`${field}_sign`];
                phoneEmailData[`${field}_data`] = sdkData.decode_params;
            }
        }
    }

    const {city, ...rest} = {
        ...(vkData && vkData.first_name
            ? {first_name: vkData.first_name}
            : sdkData.first_name
            ? {first_name: sdkData.first_name}
            : {}),
        ...(vkData && vkData.city ? {city: vkData.city} : sdkData.city ? {city: sdkData.city} : {}),
        ...(vkData && vkData.bdate ? {bdate: vkData.bdate} : sdkData.bdate ? {bdate: sdkData.bdate} : {}),
    };

    const {phone, email, phone_data, email_data, phone_sign, email_sign} = phoneEmailData;

    const result = {
        ...userInfo,
        general: {
            ...rest,
            ...(sdkData.vk_id ? {vk_id: sdkData.vk_id} : {}),
            ...(city
                ? {
                      city: {
                          id: 0,
                          title: city,
                      },
                  }
                : {}),
        },
        ...(phone ? {phone} : {}),
        ...(email ? {email} : {}),
        // подписи есть если данные маскированы
        ...(phone_sign ? {phone_hash: phone_sign} : {}),
        ...(email_sign ? {email_hash: email_sign} : {}),
        // данные для расшифровки
        ...(phone_data ? {phone_data} : {}),
        ...(email_data ? {email_data} : {}),
    };

    // window.addDebugData(`mergeres: ${JSON.stringify(result)}`);
    return result;
};

const needDebugInfo = false;

// функция которая рисует квадратик с данными, полезно для отладки на Ios мобилах
// добавляет addDebugData в глобальный объект, который можно использовать для вывода на экран
export function initDebugInfo() {
    let div: HTMLDivElement | null = null;
    if (needDebugInfo) {
        div = document.createElement('div');
        div.style.position = 'fixed';
        div.style.top = '0';
        div.style.left = '0';
        div.style.width = '100%';
        div.style.height = 'auto';
        div.style.maxHeight = '300px';
        div.style.backgroundColor = 'red';
        div.style.zIndex = '1000';
        // div.style.pointerEvents = 'none';
        div.style.opacity = '0.5';
        document.body.appendChild(div);
    }

    window.addDebugData = (data: string) => {
        if (!div) {
            return;
        }

        const newDiv = document.createElement('div');
        newDiv.innerHTML = `\n${data}\n`;
        div.appendChild(newDiv);
    };
}
export const formatResourceErrorEntry = (errorEvent: ErrorEvent): LoadResourceError => {
    if (!errorEvent.target) {
        throw new Error('Error event target is not defined');
    }

    let type: LoadResourceErrorType = 'other';
    let url = '';

    if (errorEvent.target instanceof HTMLImageElement) {
        type = 'img';
        url = errorEvent.target.src;
    } else if (errorEvent.target instanceof HTMLLinkElement) {
        type = errorEvent.target.rel === 'preload' ? 'img' : 'css';
        url = errorEvent.target.href;
    } else if (errorEvent.target instanceof HTMLScriptElement) {
        type = 'script';
        url = errorEvent.target.src;
    } else if (errorEvent.target instanceof HTMLVideoElement) {
        type = 'video';
        url = errorEvent.target.src;
    }

    return {
        type,
        url,
        ...(errorEvent.message && {message: errorEvent.message}),
    };
};

const roundMetric = (metricData: number) => Math.round(metricData * 100) / 100;

export const formatResourceTimingEntry = (entry: PerformanceResourceTiming): LoadResourceMetrics => {
    const needDebug = false;

    let entryName: ResourceMetaType = 'other';
    const isImgPreload = entry.initiatorType === 'link' && entry.name.includes('.jpeg');
    if (entry.initiatorType === 'img' || isImgPreload) {
        entryName = 'img';
    } else if (entry.initiatorType === 'script') {
        entryName = 'script';
    } else if (entry.name.includes('favicon.ico')) {
        entryName = 'favicon';
    } else if (entry.initiatorType === 'link') {
        entryName = 'css';
    } else if (entry.initiatorType === 'fetch') {
        entryName = 'fetch';
    }

    // 2. Вычисляем ключевые метрики
    const metrics = {
        transfer_size: roundMetric(entry.transferSize / 1000), // kb
        size: roundMetric((entry.decodedBodySize || 0) / 1000), // kb
        time_in_ms: roundMetric(entry.duration),
        type: entryName,
        // Разбивка по этапам (все значения относительно startTime)
        phases: {
            // DNS-запрос
            dns_lookup: roundMetric(entry.domainLookupEnd - entry.domainLookupStart),
            // Установка TCP-соединения
            tcp_handshake: roundMetric(entry.connectEnd - entry.connectStart),

            // TLS-рукопожатие (если есть)
            ssl_negotiation:
                entry.secureConnectionStart > 0 ? roundMetric(entry.connectEnd - entry.secureConnectionStart) : 0,

            // Время до первого байта (TTFB)
            waiting: roundMetric(entry.responseStart - entry.requestStart),

            // Скачивание контента
            content_download: roundMetric(entry.responseEnd - entry.responseStart),
        },

        // Абсолютные временные метки (относительно начала навигации)
        // timestamps: {
        //     fetchStart: roundMetric(entry.fetchStart),
        //     requestStart: roundMetric(entry.requestStart),
        //     responseStart: roundMetric(entry.responseStart),
        //     responseEnd: roundMetric(entry.responseEnd),
        // },
        url: entry.name,
    };

    if (needDebug) {
        if (entryName === 'script' || entryName === 'css') {
            console.table({
                Этап: [
                    'type',
                    'DNS-поиск',
                    'TCP-соединение',
                    'TLS-рукопожатие',
                    'Ожидание (TTFB)',
                    'Загрузка контента',
                    'Общее время',
                    'Скорость kb/ms',
                ],
                Длительность: [
                    entryName,
                    metrics.phases.dns_lookup,
                    metrics.phases.tcp_handshake,
                    metrics.phases.ssl_negotiation,
                    metrics.phases.waiting,
                    metrics.phases.content_download,
                    metrics.time_in_ms,
                    Math.round((metrics.transfer_size / metrics.phases.content_download) * 100) / 100,
                ],
            });
        }

        // console.log(
        //     `transferSize: ${entry.transferSize}; encodedBodySize: ${entry.encodedBodySize}; decodedBodySize: ${entry.decodedBodySize};`,
        // );

        // console.log(
        //     `Детальная статистика загрузки ${metrics.transfer_size === 0 ? 'из кэша' : 'из сети'} ${entryName}:`,
        //     metrics,
        // );
    }

    return metrics;
};
